import { useContext, useEffect, useRef } from "react";

import { IconSearch, IconX } from "@tabler/icons";
import styled, { useTheme } from "styled-components";

import { TableContext } from "@sso/context/tableContext";
import { useAppDispatch } from "@sso/hooks/useRedux";
import { SearchParameterKeys, SingleValueSearchColumn } from "@sso/types";
import { Flex } from "@sso/ui/FlexBox";
import { IconButton } from "@sso/ui/IconButton";
import { RawInput } from "@sso/ui/RawInput";

const TableSearchContainer = styled.form`
  width: 60%;
  max-width: 450px;
  padding: 0 0.2rem;
  display: flex;
  position: relative;
  @media (max-width: ${p => p.theme.breakpoints.sm}) {
    width: 100%;
    max-width: 100%;
  }
`;

const InputSearch = styled(RawInput)`
  width: 100%;
  padding: 0.4rem 80px 0.4rem 0.4rem;
  color: ${p => p.theme.colors.grey[600]};
  transition: width 0.5s ease;
  ::placeholder {
    font-size: 1rem;
    font-family: "Roboto";
  }
`;

interface TableSearchTextProps {
  name: string;
  column: string;
  searchType: SearchParameterKeys;
  nullable: boolean;
}

export const TableSearchText = ({ column, name, searchType, nullable }: TableSearchTextProps) => {
  const { colors } = useTheme();
  const dispatch = useAppDispatch();
  const { search } = useContext(TableContext);
  const searchInputRef = useRef<HTMLInputElement>(null!);

  const searchField = search && (search.object[column] as SingleValueSearchColumn);

  useEffect(() => {
    if (!searchField || !searchField.value) {
      searchInputRef.current.value = "";
      return;
    }
    const previousSearchValue = searchField.value.replaceAll("'", "");
    searchInputRef.current.value = searchField.column === column ? previousSearchValue : "";
  }, [column, searchField]);

  const handleSetSearch = () => {
    const inputValue = searchInputRef.current.value;
    if (!search || !inputValue || (inputValue === searchField?.value && column === searchField?.column)) {
      return;
    }
    //To handle search with space we have to wrap the search value between single quotation
    let searchValue = inputValue;
    if (searchValue.includes(" ")) {
      searchValue = `'${searchValue}'`;
    }

    dispatch(search.onChange({ column, value: searchValue, title: `${name}: ${searchValue}`, parameter: searchType, nullable: nullable }));
  };

  const handleClearSearch = () => {
    searchInputRef.current.value = "";
    if (!search) {
      return;
    }
    dispatch(search.onClear(column));
  };

  const handleSubmitSearch = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    handleSetSearch();
  };

  return (
    <TableSearchContainer onSubmit={handleSubmitSearch}>
      <InputSearch ref={searchInputRef} placeholder={`Search in ${name.toLowerCase()} ${nullable ? "or type N/A" : ""}`} />
      <Flex position="absolute" top="15%" right="10px">
        <IconButton
          type="submit"
          color={colors.grey.default}
          hoverColor={colors.success.main}
          onClick={handleSetSearch}
        >
          <IconSearch />
        </IconButton>
        <IconButton color={colors.grey.default} hoverColor={colors.error.main} onClick={handleClearSearch}>
          <IconX />
        </IconButton>
      </Flex>
    </TableSearchContainer>
  );
};
